// guest-mode/shell.jsx — full-takeover Guest Mode surface.
//
// Spec: BRIEF-guest-mode.md. Three HA entities drive everything:
//   - input_boolean.guest_mode (truth source)
//   - input_datetime.guest_mode_{start,end} (optional auto-flip schedule)
//
// The boolean is the only gate consumers respect; dates just feed two
// background automations that flip it at wall-clock match. Manual flip
// always wins, so the UI prefers explicit turn_on / turn_off over toggle
// (brief §5 — toggle would race with optimistic retries).

import * as React from 'react';
import { TakeoverShell, Card, ALabel, A_FONT, fs } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';

const ENTITY = {
  bool:  'input_boolean.guest_mode',
  start: 'input_datetime.guest_mode_start',
  end:   'input_datetime.guest_mode_end',
};

// ── HA <-> JS date helpers ────────────────────────────────────────────────
//
// HA exposes input_datetime state as "YYYY-MM-DD HH:MM:SS" (local wall-
// clock, no timezone). Set service expects the same string. Native
// <input type="datetime-local"> uses "YYYY-MM-DDTHH:MM" (no seconds).

function parseHaDateTime(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  return new Date(+y, +mo - 1, +d, +h, +mi, +(se || 0));
}

function toHaString(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
       + `${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

function toInputValue(date) {
  // datetime-local needs "YYYY-MM-DDTHH:MM" with no timezone suffix.
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
       + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatPretty(date) {
  // "Fri 5 Jun, 4:00 PM"
  const d = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const t = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${d}, ${t}`;
}

// ── Quick-set presets ─────────────────────────────────────────────────────
//
// Each preset returns { start, end } as Date objects. "Rounded sensibly"
// per brief §3 nice-to-have: morning checkout times round to 11:00, the
// weekend preset anchors on Fri 16:00 / Sun 11:00.

function presetTonight() {
  const start = new Date();
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  end.setHours(11, 0, 0, 0);
  return { start, end };
}

function presetWeekend() {
  const now = new Date();
  const dow = now.getDay();           // 0 Sun … 6 Sat
  const start = new Date(now);
  // If it's Mon-Thu, anchor on upcoming Fri 16:00. Fri/Sat → start now.
  // Sun → next Fri.
  if (dow >= 1 && dow <= 4) {
    start.setDate(start.getDate() + (5 - dow));
    start.setHours(16, 0, 0, 0);
  } else if (dow === 0) {
    start.setDate(start.getDate() + 5);
    start.setHours(16, 0, 0, 0);
  }
  const end = new Date(start);
  // Days forward to upcoming Sunday 11:00 from `start`.
  const startDow = start.getDay();
  end.setDate(end.getDate() + ((7 - startDow) % 7 || 7));
  end.setHours(11, 0, 0, 0);
  return { start, end };
}

function presetPlusDays(days) {
  const start = new Date();
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return { start, end };
}

// ── Schedule summary logic (brief §3.4) ───────────────────────────────────

function scheduleSummary(active, start, end) {
  const now = new Date();
  if (!active && start && start > now) {
    return { kind: 'will-on', text: `Will turn on ${formatPretty(start)}` };
  }
  if (active && end && end > now) {
    return { kind: 'will-off', text: `Will turn off ${formatPretty(end)}` };
  }
  return null;
}

// ── Component ────────────────────────────────────────────────────────────

export function GuestModeShell({ T, open, onClose, hass, viewport }) {
  if (!open) return null;

  const bool  = hass?.states?.[ENTITY.bool];
  const sRaw  = hass?.states?.[ENTITY.start];
  const eRaw  = hass?.states?.[ENTITY.end];
  const active = bool?.state === 'on';

  const startDate = sRaw?.state ? parseHaDateTime(sRaw.state) : null;
  const endDate   = eRaw?.state ? parseHaDateTime(eRaw.state) : null;

  const setBool = (on) => {
    hass?.callService?.('input_boolean', on ? 'turn_on' : 'turn_off', { entity_id: ENTITY.bool });
  };
  const setDt = (entity_id, date) => {
    hass?.callService?.('input_datetime', 'set_datetime', { entity_id, datetime: toHaString(date) });
  };
  const applyPreset = ({ start, end }) => {
    setDt(ENTITY.start, start);
    setDt(ENTITY.end, end);
  };

  const summary = scheduleSummary(active, startDate, endDate);
  const invertedRange = startDate && endDate && endDate < startDate;
  const bothPast = startDate && endDate && startDate < new Date() && endDate < new Date();

  return (
    <TakeoverShell T={T} viewport={viewport}
      eyebrow="House mode" title="Guest Mode" onClose={onClose}>

      <Pill T={T} active={active} onClick={() => setBool(!active)}/>

      <SummaryLine T={T} summary={summary} invertedRange={invertedRange} bothPast={bothPast}/>

      <Card T={T} eyebrow="Schedule" style={{ marginTop: 18 }}>
        <DateRow T={T} label="From"  date={startDate}
          onChange={(d) => setDt(ENTITY.start, d)}/>
        <DateRow T={T} label="Until" date={endDate}
          onChange={(d) => setDt(ENTITY.end, d)}/>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4,
        }}>
          <Chip T={T} label="Tonight only"  onClick={() => applyPreset(presetTonight())}/>
          <Chip T={T} label="This weekend"  onClick={() => applyPreset(presetWeekend())}/>
          <Chip T={T} label="+3 days"       onClick={() => applyPreset(presetPlusDays(3))}/>
          <Chip T={T} label="+1 week"       onClick={() => applyPreset(presetPlusDays(7))}/>
        </div>
      </Card>

      <Card T={T} eyebrow="What's affected" style={{ marginTop: 14 }}>
        <p style={{
          margin: 0, color: T.inkSoft, fontSize: fs(T, 13), lineHeight: 1.55,
        }}>
          Guest Mode suppresses a narrow set of automations affecting only the
          Guest Room + Guest Ensuite. When on:
        </p>
        <ul style={{
          margin: 0, paddingLeft: 18, color: T.inkSoft, fontSize: fs(T, 13), lineHeight: 1.6,
        }}>
          <li>Goodnight skips the Guest Room (bathroom fan + bedroom downlights stay).</li>
          <li>Guest-room blinds skip morning-open (no popping open on a sleeping guest).</li>
          <li>Guest-room blinds skip sun-tracking (no visibly-moving blinds during the day).</li>
        </ul>
        <p style={{
          margin: 0, color: T.mute, fontSize: fs(T, 12), lineHeight: 1.55,
        }}>
          Sunset privacy close still runs. No other rooms are affected.
        </p>
      </Card>
    </TakeoverShell>
  );
}

// ── Pill — mode indicator, also tap-to-flip ──────────────────────────────
//
// A large key: resting white when Guest Mode is off, joinery black (the
// selected fill) when it is on. The state word is the headline.

function Pill({ T, active, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={active}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        margin: '8px auto 0', padding: '18px 32px',
        minWidth: 280, borderRadius: RADIUS.card, border: 'none',
        background: active ? T.selected : T.keyFill,
        color: active ? T.selectedInk : T.ink,
        boxShadow: active ? 'none' : T.keyShadow,
        cursor: 'pointer',
        transition: 'background .25s, color .25s, box-shadow .25s',
      }}>
      <GuestIcon size={28}/>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500,
          letterSpacing: '.12em', textTransform: 'uppercase', opacity: .85,
        }}>
          Guest Mode
        </div>
        <div style={{
          fontFamily: A_FONT.serif, fontSize: 32,
          fontWeight: 500, lineHeight: 1.05, marginTop: 2,
        }}>
          {active ? 'On' : 'Off'}
        </div>
      </div>
    </button>
  );
}

function GuestIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2"/>
      <path d="M2.5 20c.5-3.4 3.2-5.5 6.5-5.5s6 2.1 6.5 5.5"/>
      <circle cx="17" cy="6.5" r="2.4"/>
      <path d="M15.5 13.4c2.6 0 4.5 1.6 5 4.1"/>
    </svg>
  );
}

// ── Summary + warnings ───────────────────────────────────────────────────

function SummaryLine({ T, summary, invertedRange, bothPast }) {
  // The summary line is the highest-signal element on the screen (brief
  // §3.4 + §6). Give it room and a serif treatment.
  if (!summary && !invertedRange && !bothPast) {
    return <div style={{ height: 32 }}/>; // keep vertical rhythm
  }
  return (
    <div style={{
      textAlign: 'center', marginTop: 18, marginBottom: 4,
      display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center',
    }}>
      {summary && (
        <div style={{
          fontFamily: A_FONT.serif, fontWeight: 500,
          fontSize: 26, color: T.ink, lineHeight: 1.2,
        }}>
          {summary.text}
        </div>
      )}
      {invertedRange && (
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.attention,
        }}>
          End is before start — nothing will auto-fire
        </div>
      )}
      {!summary && !invertedRange && bothPast && (
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15),
          color: T.inkSoft,
        }}>
          Past dates — manual flip only
        </div>
      )}
    </div>
  );
}

// ── Date row ─────────────────────────────────────────────────────────────

function DateRow({ T, label, date, onChange }) {
  const value = date ? toInputValue(date) : '';
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '4px 0',
    }}>
      <span style={{
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        color: T.ink, width: 64, flexShrink: 0,
      }}>
        {label}
      </span>
      <input type="datetime-local" value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          const d = new Date(v);
          if (!isNaN(d.getTime())) onChange(d);
        }}
        style={{
          flex: 1, minWidth: 0, height: 48, boxSizing: 'border-box',
          background: T.cardSoft,
          border: 'none', borderRadius: RADIUS.key + 2,
          padding: '0 14px',
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          color: T.ink,
          colorScheme: 'light dark',
        }}/>
    </label>
  );
}

// ── Quick-set chip ───────────────────────────────────────────────────────

function Chip({ T, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      height: 44, padding: '0 16px', borderRadius: RADIUS.control,
      border: 'none',
      background: T.cardSoft, color: T.ink,
      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
      cursor: 'pointer',
    }}>{label}</button>
  );
}
